import { createFileRoute, Link } from "@tanstack/react-router";
import { runIntegrityChecks } from "@/lib/data-integrity";

export const Route = createFileRoute("/data-integrity")({
  head: () => ({
    meta: [
      { title: "Data Integrity Checks — Pal Stars" },
      { name: "description", content: "Automated data integrity report for the Pal Stars Palworld database: Pals, guides, TCG card codes and internal links." },
      { property: "og:title", content: "Data Integrity Checks — Pal Stars" },
      { property: "og:description", content: "Automated integrity report for the Pal Stars Palworld database." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/data-integrity" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/data-integrity" }],
  }),
  component: DataIntegrityPage,
});

function DataIntegrityPage() {
  const checks = runIntegrityChecks();
  const errors = checks.reduce((n, c) => n + c.issues.filter((i) => i.severity === "error").length, 0);
  const warns = checks.reduce((n, c) => n + c.issues.filter((i) => i.severity === "warn").length, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Data Integrity Checks</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Validates every Pal record, guide, official TCG card code and internal guide link on each build.
      </p>
      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <span className={`rounded-full px-3 py-1 ${errors ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>
          Errors {errors}
        </span>
        <span className="rounded-full bg-amber-500/15 text-amber-400 px-3 py-1">Warnings {warns}</span>
        <Link to="/link-audit" className="rounded-full bg-muted px-3 py-1 text-primary hover:underline">
          Run live link audit →
        </Link>
      </div>

      <div className="mt-6 space-y-4">
        {checks.map((c) => {
          const errs = c.issues.filter((i) => i.severity === "error");
          const warnings = c.issues.filter((i) => i.severity === "warn");
          return (
            <section key={c.name} className="glass rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold">{c.name}</h2>
                <span className={c.passed ? "text-emerald-400 text-sm" : "text-rose-400 text-sm"}>
                  {c.passed ? "PASS" : "FAIL"}
                </span>
              </div>
              {errs.length === 0 && warnings.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No issues found.</p>
              ) : (
                <ul className="mt-2 space-y-1 text-sm">
                  {[...errs, ...warnings].slice(0, 40).map((i, idx) => (
                    <li key={idx} className={i.severity === "error" ? "text-rose-400" : "text-amber-400"}>
                      [{i.severity}] {i.message}
                    </li>
                  ))}
                  {errs.length + warnings.length > 40 && (
                    <li className="text-muted-foreground">
                      …and {errs.length + warnings.length - 40} more
                    </li>
                  )}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
